import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, DollarSign, Plus, Unlock, Pencil, Send, BellRing } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Vencimiento from '../../components/Vencimiento';
import FechaPago from '../../components/FechaPago';
import NombreCliente from '../../components/NombreCliente';
import RangoFechas, { rangoInicial, AGENCIA } from '../../components/RangoFechas';
import InputMonto from '../../components/InputMonto';
import { money, moneyK, fecha, fechaCorta, hoy, Title, Badge, Empty, ESTADO_COBRO, useOrdenFecha, Importe, importe } from './ui';

export default function AgenciaCobros() {
  const navigate = useNavigate();
  const [data, setData] = useState({ cobros: [], total: 0, cantidad: 0 });
  const [rango, setRango] = useState(() => rangoInicial('todo'));
  const [f, setF] = useState({ estado: '', search: '', desde: '', hasta: '' });
  const [listas, setListas] = useState({ medios: [], servicios: [] });
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);
  const { ordenadas: cobrosOrdenados, thOrden } = useOrdenFecha(data.cobros);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/agency/cobros', { params: { ...f, desde: rango.desde, hasta: rango.hasta } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [f, rango.desde, rango.hasta]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/agency/listas').then(r => setListas(r.data)).catch(() => {}); }, []);

  /* El botón se bloquea mientras guarda.

     Sin esto, apretarlo dos veces registra dos pagos: pasó tres veces, y en un
     caso un cobro de $195.000 quedó con $390.000 imputados. El backend además
     rechaza el pago repetido, así que si el aviso llega es porque ya estaba. */
  const [guardandoPago, setGuardandoPago] = useState(false);
  const registrarPago = async (e) => {
    e.preventDefault();
    if (guardandoPago) return;
    setGuardandoPago(true);
    try {
      const { data } = await api.post(`/agency/cobros/${pago.id}/pagos`, pago);
      toast.success(data.aviso ? `Pago registrado · le avisamos a ${data.aviso.join(', ')}` : 'Pago registrado');
      setPago(null); load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
      if (err.response?.data?.ya_registrado) load();
    } finally { setGuardandoPago(false); }
  };
  // el estado se puede cambiar siempre; para marcarlo pagado pedimos fecha, monto y medio
  const habilitar = async (c) => {
    const avisar = window.confirm(`${c.tc_provisorio ? `OJO: el tipo de cambio (${c.tipo_cambio}) es provisorio, el del cobro anterior. Si no lo revisaste, cancelá y corregilo tocando el importe.\n\n` : ''}Se habilita "${c.concepto}" de ${importe(c)} y pasa a ser cobrable.\n\n¿Le avisamos al cliente por mail?\n\nAceptar: se habilita y le avisamos.\nCancelar: se habilita sin avisarle.`);
    try {
      const { data } = await api.post(`/agency/cobros/${c.id}/habilitar`, { avisar });
      toast.success(data.aviso ? 'Habilitado · le avisamos a ' + data.aviso.join(', ') : 'Habilitado');
      load();
    } catch { toast.error('Error'); }
  };
  // Corregir el tipo de cambio de un cobro en otra moneda: recalcula los pesos y saca el "provisorio"
  const cambiarTc = async (c, tc) => {
    try { await api.put('/agency/cobros/' + c.id, { tipo_cambio: tc }); toast.success('Tipo de cambio actualizado'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'No se pudo guardar'); }
  };
  const [venc, setVenc] = useState(null);
  const guardarVencimiento = async (e) => {
    e.preventDefault();
    try {
      await api.put('/agency/cobros/' + venc.id, { vencimiento: venc.fecha || null });
      toast.success('Vencimiento actualizado');
      setVenc(null); load();
    } catch { toast.error('No se pudo guardar'); }
  };
  /* Dispara el aviso a mano. Sirve para el cobro que se habilitó sin avisar y
     para recordarle al que ya se le avisó y no pagó: es el mismo mail, cambia
     el texto. Va con la factura adjunta si está cargada. */
  const [avisando, setAvisando] = useState(null);
  const avisarCobro = async (c) => {
    const recordatorio = c.avisos > 0;
    const texto = recordatorio
      ? `¿Mandarle a ${c.cliente} un recordatorio de que ${c.concepto || 'este cobro'} sigue impago?`
      : `¿Avisarle a ${c.cliente} que tiene ${c.concepto || 'este cobro'} de ${importe(c)} para pagar?`;
    if (!window.confirm(texto)) return;
    setAvisando(c.id);
    try {
      const { data } = await api.post(`/agency/cobros/${c.id}/avisar`, { recordatorio });
      toast.success(`Le avisamos a ${(data.destinatarios || []).join(', ')}` + (data.con_factura ? ' · con la factura adjunta' : ''));
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo enviar'); }
    finally { setAvisando(null); }
  };

  const marcarEstado = async (c, estado) => {
    if (estado === c.estado) return;
    if (estado === 'pagado') return setPago({ id: c.id, fecha: hoy(), monto: c.monto, yaPago: c.pagado_el });
    try {
      const { data } = await api.put('/agency/cobros/' + c.id, { estado });
      toast.success(data.revertido ? 'Se quitó el pago registrado' : 'Estado actualizado');
      load();
    } catch { toast.error('Error'); }
  };

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const vencidos = data.cobros.filter(c => c.habilitado && ['pendiente', 'vencido'].includes(c.estado) && c.vencimiento && c.vencimiento.slice(0, 10) < hoy());

  return (
    <div>
      <Title>Cobros</Title>

      <RangoFechas valor={rango} onChange={setRango} opciones={AGENCIA} />

      {/* La foto del período elegido. No respeta el filtro de estado a propósito:
          sirve para ver el total mientras abajo mirás una parte. */}
      {data.resumen && (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline',
          margin: '2px 2px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>
          {[
            ['Cobrado', data.resumen.cobrado, data.resumen.cant_cobrado, '#22c55e', 'pagado'],
            ['Pendiente', data.resumen.pendiente, data.resumen.cant_pendiente, '#f59e0b', 'abiertos'],
            ['Vencido', data.resumen.vencido, null, '#ef4444', 'abiertos'],
            ['A pasar', data.resumen.a_pasar, data.resumen.cant_a_pasar, '#64748b', 'a_pasar'],
          ].map(([etiqueta, monto, cant, color, filtro]) => (
            <button key={etiqueta} onClick={() => setF({ ...f, estado: filtro })}
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
              title={`Ver solo ${etiqueta.toLowerCase()}`}>
              <span>{etiqueta} </span>
              <b style={{ color, fontSize: 15, fontWeight: 800 }}>{moneyK(monto)}</b>
              {cant !== null && cant > 0 && <span style={{ fontSize: 11 }}> · {cant}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16, padding: '12px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 300 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--text-muted)' }} />
          <input value={f.search} onChange={e => setF({ ...f, search: e.target.value })} placeholder="Buscar cliente o concepto" style={{ paddingLeft: 32, fontSize: 13 }} />
        </div>
        <select value={f.estado} onChange={e => setF({ ...f, estado: e.target.value })} style={{ width: 'auto', fontSize: 13 }}>
          <option value="abiertos">Por cobrar</option>
          <option value="a_pasar">A pasar (futuros)</option>
          <option value="">Todos</option>
          <option value="pagado">Pagados</option>
          <option value="pendiente">Pendientes</option>
          <option value="informado">Informados por el cliente</option>
          <option value="anulado">Anulados</option>
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 13 }}>
          <b>{data.cantidad}</b> cobros · <b>{moneyK(data.total)}</b>
          {vencidos.length > 0 && <span style={{ color: '#ef4444', marginLeft: 10 }}>{vencidos.length} vencidos</span>}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Cliente</th><th style={th}>Concepto</th>{thOrden('emitido', 'Emitido', th)}{thOrden('vencimiento', 'Vence', th)}
              <th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}>Factura</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} style={{ ...td, color: 'var(--text-muted)' }}>Cargando...</td></tr>
                : data.cobros.length === 0 ? <tr><td colSpan={8} style={td}><Empty>No hay cobros con ese filtro</Empty></td></tr>
                : cobrosOrdenados.map(c => {
                  const e = ESTADO_COBRO[c.estado] || {};
                  const vencido = c.habilitado && ['pendiente', 'vencido'].includes(c.estado) && c.vencimiento && c.vencimiento.slice(0, 10) < hoy();
                  return (
                    <tr key={c.id}>
                      <td style={{ ...td, fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.cliente_id)}><NombreCliente nombre={c.cliente} logo={c.logo_url} /></td>
                      <td style={td}>{c.concepto || c.servicio || '—'}{c.cuota_total ? ` (${c.cuota_numero}/${c.cuota_total})` : ''}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>
                        <Vencimiento cobro={c} campo="emitido" onCambio={load}>{fechaCorta(c.emitido)}</Vencimiento>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: vencido ? '#ef4444' : 'inherit' }}>
                        <button className="celda-editable" title="Cambiar el vencimiento"
                          onClick={() => setVenc({ id: c.id, cliente: c.cliente, concepto: c.concepto || c.servicio, fecha: (c.vencimiento || '').slice(0, 10) })}>
                          {fechaCorta(c.vencimiento)}<Pencil size={11} />
                        </button>
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}><Importe c={c} alCambiarTc={cambiarTc} /></td>
                      <td style={td}>
                        {/* "A pasar" y "Pendiente" son dos momentos distintos y no
                            pueden verse juntos: a pasar es que todavía no se le
                            pasó, pendiente es que ya se le pasó y no pagó.
                            Mientras está a pasar, lo único que corresponde hacer
                            es habilitarlo, así que no se ofrece cambiar el estado. */}
                        {c.estado === 'anulado' ? (
                          <Badge label="Anulado" color="#64748b" bg="rgba(100,116,139,0.15)" />
                        ) : !c.habilitado ? (
                          <Badge label="A pasar" color="#64748b" bg="rgba(100,116,139,0.15)" />
                        ) : (
                        <select value={c.estado} onChange={ev => marcarEstado(c, ev.target.value)} title="Cambiar estado"
                          style={{ width: 'auto', padding: '4px 26px 4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                            background: (vencido && c.estado === 'pendiente' ? 'rgba(239,68,68,0.15)' : e.bg) || 'transparent',
                            color: vencido && c.estado === 'pendiente' ? '#ef4444' : e.color, border: '1px solid var(--border)' }}>
                          {Object.entries(ESTADO_COBRO).map(([k, v]) => <option key={k} value={k}>{k === 'pendiente' && vencido ? 'Vencido' : v.label}</option>)}
                        </select>
                        )}
                        {/* Cuándo entró la plata. Se puede corregir: es la fecha
                            con la que el cobro cuenta en los reportes. */}
                        {c.estado === 'pagado' && (
                          <div style={{ marginTop: 3 }}><FechaPago cobro={c} editable onCambio={load} /></div>
                        )}
                      </td>
                      <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>{c.sin_factura ? 'Sin factura' : 'Con factura'}</td>
                      <td style={td}>
                        {c.estado === 'anulado' ? null : !c.habilitado
                          ? <button className="btn btn-ghost btn-sm" onClick={() => habilitar(c)} title="Pasarlo al cliente"><Unlock size={14} /> Habilitar</button>
                          : c.estado !== 'pagado'
                          ? (
                            <span style={{ display: 'inline-flex', gap: 2, whiteSpace: 'nowrap' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => setPago({ id: c.id, fecha: hoy(), monto: c.monto, yaPago: c.pagado_el })}><DollarSign size={14} /> Pagó</button>
                              <button className="btn btn-ghost btn-sm" disabled={avisando === c.id}
                                onClick={() => avisarCobro(c)}
                                title={c.avisos > 0
                                  ? `Ya se le avisó ${c.avisos} ${c.avisos === 1 ? 'vez' : 'veces'} · mandar un recordatorio`
                                  : 'Todavía no se le avisó · mandarle el aviso de pago'}>
                                {c.avisos > 0 ? <BellRing size={14} /> : <Send size={14} />}
                              </button>
                            </span>
                          )
                          : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pagado el {fecha(c.pagado_el)}</span>}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {venc && (
        <div className="modal-overlay" onClick={() => setVenc(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>Cambiar el vencimiento</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 16px' }}>
              {venc.cliente} · {venc.concepto}
            </p>
            <form onSubmit={guardarVencimiento}>
              <div className="form-group"><label>Vence el</label>
                <input type="date" value={venc.fecha || ''} autoFocus
                  onChange={e => setVenc({ ...venc, fecha: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setVenc(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pago && (
        <div className="modal-overlay" onClick={() => setPago(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>Registrar pago</h2>
            {/* Si ya tiene un pago, se avisa antes de cargar otro */}
            {pago.yaPago && (
              <div style={{ fontSize: 12.5, padding: '8px 10px', borderRadius: 8, marginBottom: 12, background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>
                Este cobro ya tiene un pago registrado el {fecha(pago.yaPago)}. Si estás cargando una cuota, seguí; si no, cancelá.
              </div>
            )}
            <form onSubmit={registrarPago}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Fecha</label><input type="date" value={pago.fecha} onChange={e => setPago({ ...pago, fecha: e.target.value })} /></div>
                <div className="form-group"><label>Monto</label><InputMonto value={pago.monto} onChange={v => setPago({ ...pago, monto: v })} /></div>
              </div>
              <div className="form-group"><label>Medio de pago</label>
                <select value={pago.medio_id || ''} onChange={e => setPago({ ...pago, medio_id: e.target.value })}>
                  <option value="">—</option>{listas.medios.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={pago.avisar !== false} onChange={e => setPago({ ...pago, avisar: e.target.checked })} style={{ width: 16, height: 16 }} />
                Avisarle al cliente que recibimos el pago
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={guardandoPago}><Plus size={14} /> {guardandoPago ? 'Registrando...' : 'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
